import Typography from '@mui/material/Typography';
import { fileSizeLabel } from '@taqon/contracts';
import { useSession } from '../session/session';

/** The largest document the API accepts, from the session; undefined until it is known. */
export function useUploadLimit(): number | undefined {
  return useSession().data?.documentMaxUploadBytes;
}

/** Why `file` cannot be uploaded under `limit`, in words; null when it may be. */
export function oversizeMessage(file: File | null, limit: number | undefined): string | null {
  if (file === null || limit === undefined || file.size <= limit) return null;
  return `This file is ${fileSizeLabel(file.size)}; the limit is ${fileSizeLabel(limit)}. Choose a smaller file.`;
}

/**
 * Under a file picker: the accepted types and the size limit, and — for a file over the limit —
 * why it cannot be uploaded (the API refuses it either way, SDD §12.5).
 */
export function FileRules({
  accepted,
  file,
  limit,
}: {
  accepted: string | null;
  file: File | null;
  limit: number | undefined;
}) {
  const parts = [
    ...(accepted === null ? [] : [`Accepted: ${accepted}`]),
    ...(limit === undefined ? [] : [`Maximum size: ${fileSizeLabel(limit)}`]),
  ];
  const problem = oversizeMessage(file, limit);
  return (
    <>
      {parts.length > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {parts.join(' · ')}
        </Typography>
      )}
      {problem !== null && (
        <Typography variant="caption" color="error" role="alert" sx={{ display: 'block' }}>
          {problem}
        </Typography>
      )}
    </>
  );
}
